import React, { useEffect, useRef, useState } from 'react'

import _ from 'lodash'
import { Map, View, Feature } from 'ol'
import Control from 'ol/control/Control.js'
import type { FeatureLike } from 'ol/Feature'
import { Point, Polygon } from 'ol/geom'
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer'
import MapBrowserEvent from 'ol/MapBrowserEvent'
import Overlay from 'ol/Overlay'
import { fromLonLat, toLonLat } from 'ol/proj'
import { OSM, Vector as VectorSource } from 'ol/source'
import XYZ from 'ol/source/XYZ'
import { Style, Icon, Fill, Stroke } from 'ol/style'

import { useApi } from '@/hooks'
import { buildQuery } from '@/insights/common/queryUtils'

import 'ol/ol.css'

export interface MapLocation {
  address: string
  lat: number
  lng: number
  markerContent?: React.ReactNode
  title?: string
  companyId?: number
}

export interface LocationMapProps {
  location: MapLocation
  markerContent?: React.ReactNode
  initialZoom?: number
  className?: string
}

interface Geofence {
  id: string
  name: string
  companyId: string
  coordinates: [number, number][]
}

interface GeofenceData {
  id: number
  name: string
}

const LocationMap: React.FC<LocationMapProps> = ({
  location,
  markerContent,
  initialZoom = 15,
  className = 'w-full h-full',
}) => {
  const { api } = useApi()
  const [useSatellite, setUseSatellite] = useState(true)
  const [geofences, setGeofences] = useState<Geofence[]>([])
  const [clickedFeature, setClickedFeature] = useState<{ type: 'marker' | 'geofence'; data?: GeofenceData } | null>(
    null
  )
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<Map | null>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<Overlay | null>(null)
  const geofenceLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const companyId = location.companyId

  const fetchGeofences = async (east: number, west: number, north: number, south: number, metersPerPixel: number) => {
    if (!companyId || !api) {
      return
    }

    try {
      const params = {
        companyId: companyId,
        east: east,
        metersPerPixel: metersPerPixel,
        north: north,
        south: south,
        west: west,
      }
      const path = `/fresh/geofences/by-boundaries?${buildQuery(params)}`
      const response = await api.get<Geofence[]>({
        path,
        origin: 'LocationMap',
      })
      setGeofences(response?.data && _.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('Error fetching geofences:', error)
      setGeofences([])
    }
  }

  const calculateAndFetchGeofences = (map: Map) => {
    if (!map || !map.getView() || !map.getSize()) {
      return
    }

    const extent = map.getView().calculateExtent(map.getSize())
    const [minX, minY, maxX, maxY] = extent
    const westLon = toLonLat([minX, 0])[0]
    const eastLon = toLonLat([maxX, 0])[0]
    const southLat = toLonLat([0, minY])[1]
    const northLat = toLonLat([0, maxY])[1]

    const view = map.getView()
    const resolution = view.getResolution() || 1
    const metersPerPixel = resolution

    fetchGeofences(eastLon, westLon, northLat, southLat, metersPerPixel)
  }

  const debouncedFetchGeofences = (map: Map) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    debounceTimeoutRef.current = setTimeout(() => {
      calculateAndFetchGeofences(map)
    }, 300)
  }

  const createGeofenceFeatures = (geofences: Geofence[]) => {
    return geofences.map((geofence) => {
      const coordinates = geofence.coordinates.map((coord) => fromLonLat([coord[1], coord[0]]))

      const polygon = new Polygon([coordinates])
      const feature = new Feature({
        geometry: polygon,
        geofenceId: geofence.id,
        geofenceName: geofence.name,
      })

      feature.setStyle(
        new Style({
          stroke: new Stroke({
            color: '#3b82f6',
            width: 2,
          }),
          fill: new Fill({
            color: 'rgba(59, 130, 246, 0.1)',
          }),
        })
      )

      return feature
    })
  }

  useEffect(() => {
    if (!mapRef.current || !location) return

    if (mapInstanceRef.current) {
      mapInstanceRef.current.setTarget(undefined)
    }

    const osmLayer = new TileLayer({
      source: new OSM({
        transition: 0,
        interpolate: false,
      }),
      visible: !useSatellite,
    })

    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attributions:
          'Tiles © Esri — Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        maxZoom: 19,
      }),
      visible: useSatellite,
    })

    const map = new Map({
      target: mapRef.current,
      layers: [osmLayer, satelliteLayer],
      view: new View({
        center: fromLonLat([location.lng, location.lat]),
        zoom: initialZoom,
        constrainResolution: true,
      }),
    })

    const handleInitialLoad = () => {
      calculateAndFetchGeofences(map)
      map.un('postrender', handleInitialLoad)
    }
    map.on('postrender', handleInitialLoad)

    const handleViewChange = () => {
      debouncedFetchGeofences(map)
    }

    map.getView().on('change:resolution', handleViewChange)
    map.getView().on('change:center', handleViewChange)

    const marker = new Feature({
      geometry: new Point(fromLonLat([location.lng, location.lat])),
    })

    marker.setStyle(
      new Style({
        image: new Icon({
          src:
            'data:image/svg+xml;base64,' +
            btoa(`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#dc2626"/>
            </svg>
          `),
          scale: 1.5,
        }),
      })
    )

    const vectorSource = new VectorSource({
      features: [marker],
    })

    const vectorLayer = new VectorLayer({
      source: vectorSource,
    })

    map.addLayer(vectorLayer)

    const geofenceSource = new VectorSource()
    const geofenceLayer = new VectorLayer({
      source: geofenceSource,
    })
    map.addLayer(geofenceLayer)
    geofenceLayerRef.current = geofenceLayer

    geofenceLayer.setZIndex(1)
    vectorLayer.setZIndex(2)

    mapInstanceRef.current = map

    const geolocateButton = document.createElement('button')
    geolocateButton.type = 'button'
    geolocateButton.title = 'Go to my location'
    geolocateButton.setAttribute('aria-label', 'Go to my location')
    geolocateButton.className = 'ol-geolocate-btn'
    geolocateButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="2.5"></circle>
        <circle cx="12" cy="12" r="8"></circle>
        <path d="M12 3v4"></path>
        <path d="M12 17v4"></path>
        <path d="M3 12h4"></path>
        <path d="M17 12h4"></path>
      </svg>
    `

    const geolocateElement = document.createElement('div')
    geolocateElement.className = 'ol-control ol-geolocate-control'
    geolocateElement.appendChild(geolocateButton)

    const geolocateControl = new Control({ element: geolocateElement })
    map.addControl(geolocateControl)

    geolocateButton.addEventListener('click', () => {
      if (!('geolocation' in navigator)) return
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords
          const center = fromLonLat([longitude, latitude])
          map.getView().animate({ center, zoom: Math.max(map.getView().getZoom() || 14, 14), duration: 500 })
        },
        () => {
          // ignore errors silently
        },
        { enableHighAccuracy: true, timeout: 10000 }
      )
    })

    if (popupRef.current) {
      const overlay = new Overlay({
        element: popupRef.current,
        offset: [0, -12],
        positioning: 'bottom-center',
        stopEvent: true,
      })
      overlayRef.current = overlay
      map.addOverlay(overlay)
      overlay.setPosition(undefined)
    }

    const isMarkerFeature = (feature: FeatureLike | null) => feature === marker
    const isGeofenceFeature = (feature: FeatureLike | null) => feature && feature.get('geofenceId')

    const clickHandler = (evt: MapBrowserEvent) => {
      const pixel = evt.pixel
      const hit = map.forEachFeatureAtPixel(pixel, (feature) => feature as FeatureLike | null)
      if (hit && isMarkerFeature(hit)) {
        const coord = (marker.getGeometry() as Point).getCoordinates()
        setClickedFeature({ type: 'marker' })
        overlayRef.current?.setPosition(coord)
      } else if (hit && isGeofenceFeature(hit)) {
        const coord = evt.coordinate
        const geofenceData = {
          id: hit.get('geofenceId'),
          name: hit.get('geofenceName'),
        }
        setClickedFeature({ type: 'geofence', data: geofenceData })
        overlayRef.current?.setPosition(coord)
      } else {
        setClickedFeature(null)
        overlayRef.current?.setPosition(undefined)
      }
    }

    const pointerHandler = (evt: MapBrowserEvent) => {
      const pixel = evt.pixel
      const hit = map.forEachFeatureAtPixel(pixel, (feature) => feature as FeatureLike | null)
      const target = map.getTargetElement()
      if (target) {
        target.style.cursor = hit && (isMarkerFeature(hit) || isGeofenceFeature(hit)) ? 'pointer' : ''
      }
    }

    map.on('singleclick', clickHandler)
    map.on('pointermove', pointerHandler)

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }

      if (overlayRef.current) {
        map.removeOverlay(overlayRef.current)
        overlayRef.current = null
      }

      map.un('singleclick', clickHandler)
      map.un('pointermove', pointerHandler)

      map.getView().un('change:resolution', handleViewChange)
      map.getView().un('change:center', handleViewChange)

      map.removeControl(geolocateControl)

      if (geofenceLayerRef.current) {
        geofenceLayerRef.current = null
      }

      if (mapInstanceRef.current) {
        mapInstanceRef.current.setTarget(undefined)
        mapInstanceRef.current = null
      }
    }
  }, [location, useSatellite, initialZoom])

  useEffect(() => {
    if (!geofenceLayerRef.current) return

    const source = geofenceLayerRef.current.getSource()
    if (!source) return

    source.clear()

    if (geofences.length > 0) {
      const features = createGeofenceFeatures(geofences)
      source.addFeatures(features)
    }
  }, [geofences])

  return (
    <div className={`relative ${className}`}>
      <div ref={mapRef} className="w-full h-full border border-gray-300 rounded" />

      <div className="absolute bottom-4 left-4 z-1000">
        <button
          className="px-3 py-2 text-sm font-medium bg-white text-gray-900 rounded shadow-md border border-gray-300 hover:bg-gray-50 transition-colors"
          onClick={() => setUseSatellite(!useSatellite)}
        >
          {useSatellite ? 'Map' : 'Satellite'}
        </button>
      </div>

      <style>
        {`
          .ol-popup {
            position: absolute;
            background: white;
            border-radius: 6px;
            border: 1px solid rgba(0,0,0,0.15);
            padding: 8px 12px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.15);
            font-size: 12px;
            width: max-content;
            max-width: 250px;
            white-space: nowrap;
          }

          .ol-geolocate-control {
            position: absolute;
            top: 0.5em;
            right: 0.5em;
            left: auto;
            bottom: auto;
            z-index: 1000;
          }
          .ol-geolocate-control .ol-geolocate-btn {
            background: white;
            border: 1px solid rgba(0,0,0,0.2);
            border-radius: 4px;
            width: 2em;
            height: 2em;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
        `}
      </style>
      <div ref={popupRef} className="ol-popup">
        {clickedFeature?.type === 'marker' ? (
          markerContent || (
            <div>
              <div className="font-medium mb-1">{location.address}</div>
              <div className="text-xs">
                Lat: {location.lat.toFixed(5)}, Lng: {location.lng.toFixed(5)}
              </div>
            </div>
          )
        ) : clickedFeature?.type === 'geofence' ? (
          <div>
            <div className="font-medium mb-1">{clickedFeature.data?.name}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default LocationMap
